import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Star, Loader2 } from 'lucide-react';

interface GameData {
  id: string;
  title: string;
  provider: string;
  image: string;
  rating: number;
  category: string;
  description: string;
  game_url?: string;
  // Campos adicionais para esportes
  date?: string;
  time?: string;
  oddsHome?: number;
  oddsDraw?: number | null;
  oddsAway?: number;
  // Campos adicionais para cassino ao vivo
  dealer?: string;
}

const Casino: React.FC = () => {
  const [casinoGames, setCasinoGames] = useState<GameData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCasinoGames = async () => {
      setLoading(true);
      setError(null);
      /*try {
        const response = await fetch("https://slots.virtualebank.com.br/games");
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Failed to fetch casino games: ${response.status}`);
        }
        const allGames: GameData[] = await response.json();
        // Filtrar jogos que são de cassino (Slots, Roleta, Cartas, Jackpot, etc.)
        const filteredGames = allGames.filter(game => 
          game.category === "Slots" || 
          game.category === "Roleta" || 
          game.category === "Cartas" || 
          game.category === "Jackpot"
        );
        setCasinoGames(filteredGames);
      } catch (err) {
        console.error("Error fetching casino games:", err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }*/
    };

    fetchCasinoGames();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="ml-3 text-lg text-foreground">Carregando jogos de cassino...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-center py-16">
        <h1 className="text-3xl font-bold text-red-500 mb-4">Erro ao carregar jogos de cassino</h1>
        <p className="text-muted-foreground">Ocorreu um erro: {error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Casino Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 to-pink-900 text-white py-20 md:py-32 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              Cassino Online
            </h1>
            <p className="text-lg md:text-xl mb-8 animate-fade-in-up delay-200">
              Descubra a emoção dos melhores jogos de cassino, slots, roleta, blackjack e muito mais!
            </p>
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-lg px-8 py-6 animate-fade-in-up delay-400" asChild>
              <Link to="/register">Jogue Agora</Link>
            </Button>
          </div>
        </div>
        <div className="absolute inset-0 z-0 opacity-20">
          <img src="/src/assets/game-casino.jpg" alt="Casino Background" className="w-full h-full object-cover" />
        </div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-background via-transparent to-transparent"></div>
      </section>

      {/* Game Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
            Nossas Categorias de Cassino
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {['Slots', 'Roleta', 'Blackjack', 'Jackpot', 'Cartas', 'Outros'].map((category, index) => (
              <Card key={index} className="group hover:border-primary/50 transition-all duration-300 hover:shadow-lg cursor-pointer">
                <CardContent className="p-4 text-center">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary">{category}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Casino Games Section */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
            Jogos de Cassino em Destaque
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {casinoGames.map((game) => (
              <Link to={`/game/${game.id}`} key={game.id}>
                <Card className="group relative overflow-hidden border-border hover:border-primary/50 transition-all duration-300 hover:shadow-card cursor-pointer h-full">
                  <CardContent className="p-0">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={game.image}
                        alt={game.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-glow">
                          <Play className="w-7 h-7 text-primary-foreground ml-1" fill="currentColor" />
                        </div>
                      </div>
                      <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
                        <Star className="w-3 h-3 text-accent fill-accent" />
                        <span className="text-xs font-semibold text-foreground">{game.rating}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-primary font-medium mb-1">
                        {game.provider}
                      </div>
                      <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                        {game.title}
                      </h3>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button size="lg" variant="outline" className="border-2 border-primary/50 hover:bg-primary/10 text-lg px-8 py-6" asChild>
              <Link to="/casino">Ver Todos os Jogos de Cassino</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Casino;
